import { Injectable, inject } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import { ProjectXCompoundXModel } from 'src/app/shared/models/project-x-compound-x-model';
import { ModelLibraryApiService } from 'src/app/shared/services/model-library.api.service';
import { Constants } from 'src/app/shared/utils/constants';

@Injectable({
  providedIn: 'root'
})
export class EEASetupAquaMixService {

  private readonly selectedProjectApiService = inject(SelectedProjectApiService);
  private readonly modelLibraryApiService = inject(ModelLibraryApiService);

  private readonly triggerModels: string[] = [
    Constants.MODELS.STEP_1_2,
    ...Constants.SWASH_VERSIONS,
    Constants.MODELS.SWAN
  ];

  private aquaMixModelPk?: number;

  async syncCompounds(projectPk: number, projectXCompoundXModel: ProjectXCompoundXModel[]): Promise<boolean> {
    const modelPk = await this.getAquaMixModelPk();
    if (!modelPk) return false;

    const compounds = this.getTriggeredCompounds(projectXCompoundXModel, modelPk);
    return firstValueFrom(
      this.selectedProjectApiService.syncProjectXCompoundXModelsByModel(projectPk, modelPk, compounds).pipe(take(1))
    );
  }

  private getTriggeredCompounds(projectXCompoundXModel: ProjectXCompoundXModel[], modelPk: number): ProjectXCompoundXModel[] {
    const compoundsByKey = new Map<string, ProjectXCompoundXModel>();

    projectXCompoundXModel
      .filter(compound => this.triggerModels.includes(compound.ModelName ?? ''))
      .forEach(compound => {
        const key = `${compound.MoleculePk}|${compound.MetabolitePk ?? ''}`;
        if (compoundsByKey.has(key)) return;
        compoundsByKey.set(key, {
          MoleculePk: compound.MoleculePk,
          MetabolitePk: compound.MetabolitePk,
          ModelPk: modelPk,
          AlternativeNameCompound: compound.AlternativeNameCompound,
          CompoundOrder: compound.CompoundOrder
        });
      });

    return [...compoundsByKey.values()];
  }

  private async getAquaMixModelPk(): Promise<number | undefined> {
    if (this.aquaMixModelPk !== undefined) return this.aquaMixModelPk;

    const models = await firstValueFrom(this.modelLibraryApiService.getModels().pipe(take(1)));
    this.aquaMixModelPk = models.find(model => model.name === Constants.MODELS.AGD_AQUAMIX)?.modelPk;

    return this.aquaMixModelPk;
  }
}
